import type { ClienteApi, ConVersion } from '../cliente';
import { claveIdempotencia, consulta, negocio, type Pagina } from '../comun';
import type { IdNegocio } from '../operaciones';
import type { components } from '../tipos/clientes';

type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type Solicitud = Esquema<'PrivacyRequest'>;
export type ListaSolicitudes = Esquema<'PrivacyRequestList'>;
export type EstadoSolicitud = Esquema<'PrivacyRequestStatus'>;
export type TipoSolicitud = Esquema<'PrivacyRequestType'>;
export type ParteSolicitud = Esquema<'PrivacyRequestPart'>;
export type NuevaSolicitud = Esquema<'PrivacyRequestRegister'>;
export type AjustesClientes = Esquema<'CustomerSettings'>;
export type CambiosAjustes = Esquema<'CustomerSettingsWrite'>;
export type MarcaBloqueo = Esquema<'LegalHold'>;
export type CambiosDatosLocales = Esquema<'CustomerUpdate'>;
export type FichaDeCliente = Esquema<'Customer'>;
/** `409 commitments_pending` con lo que se cancela (G27, G151). */
export type ProblemaCompromisos = Esquema<'CommitmentsProblem'>;
export type Compromiso = NonNullable<ProblemaCompromisos['commitments']>[number];

const ruta = (businessId: IdNegocio, resto: string): `/v1/${string}` => `${negocio(businessId)}${resto}`;
const id = (valor: string) => encodeURIComponent(valor);

/**
 * Operaciones de «Protección de datos» (2.7) y de sus «Plazos de conservación» (G194, G204), con su `operationId` y los
 * tipos generados de `clientes.yaml`. Atender, ampliar y rechazar solo valen en las de `handled_by: business` (G150) y
 * funcionan en solo lectura (obligación legal); los plazos se leen con su `ETag` y se cambian con `If-Match`.
 */
export function operacionesClientesPrivacidad(api: ClienteApi) {
    return {
        /** listPrivacyRequests (`clientes.rgpd.atender`): por estado, sin total (G140). */
        listPrivacyRequests: (businessId: IdNegocio, estado: EstadoSolicitud | null, pagina: Pagina = {}) =>
            api.pedir<ListaSolicitudes>('GET', ruta(businessId, `/privacy-requests${consulta({ status: estado, ...pagina })}`)),
        /** getPrivacyRequest (`clientes.rgpd.atender`): con las partes de cada módulo, `details` e `identity_evidence_ref` (G151). */
        getPrivacyRequest: (businessId: IdNegocio, requestId: string) => api.pedir<Solicitud>('GET', ruta(businessId, `/privacy-requests/${id(requestId)}`)),
        /** registerPrivacyRequest (`clientes.rgpd.atender`): la que llega por otra vía; plazo de un mes desde `received_at`. */
        registerPrivacyRequest: (businessId: IdNegocio, datos: NuevaSolicitud, clave = claveIdempotencia()) =>
            api.pedir<Solicitud>('POST', ruta(businessId, '/privacy-requests'), { cuerpo: datos, idempotencia: clave }),
        /**
         * executePrivacyRequest (`clientes.rgpd.atender`): copia, corrección o supresión. Con compromisos activos,
         * `409 commitments_pending` con la lista; se repite con `confirm_cancellations: true` (G27). Sin «Deshacer» (G159).
         */
        executePrivacyRequest: (businessId: IdNegocio, requestId: string, confirmarCancelaciones: boolean, clave = claveIdempotencia()) =>
            api.pedir<Solicitud>('POST', ruta(businessId, `/privacy-requests/${id(requestId)}/execution`), { cuerpo: { confirm_cancellations: confirmarCancelaciones }, idempotencia: clave }),
        /** extendPrivacyRequest (`clientes.rgpd.atender`): hasta dos meses más, una vez, dentro del primer mes (G199). */
        extendPrivacyRequest: (businessId: IdNegocio, requestId: string, motivo: string, clave = claveIdempotencia()) =>
            api.pedir<Solicitud>('POST', ruta(businessId, `/privacy-requests/${id(requestId)}/extension`), { cuerpo: { reason: motivo }, idempotencia: clave }),
        /** rejectPrivacyRequest (`clientes.rgpd.atender`): el cliente recibe el motivo y el aviso de la AEPD (N6, G199). */
        rejectPrivacyRequest: (businessId: IdNegocio, requestId: string, motivo: string, clave = claveIdempotencia()) =>
            api.pedir<Solicitud>('POST', ruta(businessId, `/privacy-requests/${id(requestId)}/rejection`), { cuerpo: { reason: motivo }, idempotencia: clave }),
        // ---- Plazos de conservación (G194, G204) ----
        /** getCustomerSettings (`clientes.rgpd.atender`): sin ajustes guardados, los de por defecto con `version = 0`. */
        getCustomerSettings: (businessId: IdNegocio): Promise<ConVersion<AjustesClientes>> => api.pedirConVersion<AjustesClientes>('GET', ruta(businessId, '/customer-settings')),
        /** updateCustomerSettings (`clientes.rgpd.atender`): con `If-Match`; 412 si otra persona los cambió antes. */
        updateCustomerSettings: (businessId: IdNegocio, cambios: CambiosAjustes, version: string, clave = claveIdempotencia()) =>
            api.pedirConVersion<AjustesClientes>('PUT', ruta(businessId, '/customer-settings'), { cuerpo: cambios, siCoincide: version, idempotencia: clave }),
    };
}
